import { fabric } from 'fabric';
import { cloneDeep } from 'lodash';

import { DeviceSearchRequest } from 'api/DeviceRequest';
import i18next from 'i18n/i18n';
import { AcceptUser } from 'model/AcceptUser';
import { AcceptUserInfo } from 'model/AcceptUserInfo';
import Equipment from 'model/Equipment';
import { LockingInfo } from 'model/LockingInfo';
import { LotoBook } from 'model/LotoBook';
import { UserInfo } from 'recoil/user/atom';
import { DEPT_TYPE, DEVICE_MODAL_TYPE, LOTO_TYPE, ROLE_TYPE, VCM_LOCK_TYPE, YN_TYPE } from 'types/enumType';

import { decryptId, decryptString, encryptString } from './encryptUtil';

export const MODAL_SIZE = 1800;
export const MIN_SCALE = 0.2; // 임의 설정, 너무 작지 않게
export const SIDE_BAR = 200;

export const convertStringToNumber = (value: string) => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? 0 : num;
};

export const convertYnToBoolean = (value: YN_TYPE): boolean =>
  value === YN_TYPE.Y;
export const convertBooleanToYn = (value: boolean): YN_TYPE =>
  value ? YN_TYPE.Y : YN_TYPE.N;

export const convertDeviceTypeToString = (deviceType: DEVICE_MODAL_TYPE) => {
  switch (deviceType) {
    case DEVICE_MODAL_TYPE.PART:
      return i18next.t('user.factory');
    case DEVICE_MODAL_TYPE.AREA:
      return i18next.t('device.area');
    case DEVICE_MODAL_TYPE.SECTION:
      return i18next.t('device.zone');
    default:
      return i18next.t('device.device');
  }
};


export const isLockedByVcmLock = (vcmLockInfo: VCM_LOCK_TYPE) => {
  if (vcmLockInfo === null) {
    return '';
  }

  switch (vcmLockInfo) {
    case VCM_LOCK_TYPE.LOCK:
      return 'lock';
    case VCM_LOCK_TYPE.UNLOCK:
      return 'unlock';
    case VCM_LOCK_TYPE.SET:
      return 'set';
    default:
      return '';
  }
};

export const isLockedByLockingInfo = (lockingInfo: LockingInfo) => {
  if (!lockingInfo || !(lockingInfo.lockRegDate || lockingInfo.unlockRegDate)) {
    return '';
  }

  const { lockRegDate, unlockRegDate } = lockingInfo;
  return lockRegDate && !unlockRegDate ? 'lock' : 'unlock';
};

export const isLockedByLockingInfoList = (lockingInfoList: LockingInfo[]) => {
  if (!lockingInfoList || lockingInfoList.length === 0) {
    return false;
  }

  return lockingInfoList.some(lockingInfo =>
    isLockedByLockingInfo(lockingInfo) === 'lock' || !isLockedByLockingInfo(lockingInfo),
  );
};

export const drawLockingInfo = (info: LockingInfo) => {
  if (!info) {
    return '';
  }
  let lockedInfoString = `${info.lockUserName} / ${info.lockRegDate}`;
  lockedInfoString += !info.unlockRegDate
    ? ''
    : `\n\r${info.unlockUserName} / ${info.unlockRegDate}`;

  return lockedInfoString;
};

const generateLotoActionNoByLockInfo = (
  lockInfo: LockingInfo[],
  startIndex: number,
): number => {
  let acc = startIndex;
  if (lockInfo !== null && lockInfo?.length > 0) {
    lockInfo?.forEach(item => {
      item.no = acc;
      acc += 1;
    });
  }
  return acc;
};

export const generateLogInfoNo = (logInfo: LotoBook): LotoBook => {
  let index = 1;
  const cloneLogInfo = cloneDeep(logInfo);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.prodLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.elecLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.mechLocks, index);
  index = generateLotoActionNoByLockInfo(cloneLogInfo.etcLocks, index);

  return cloneLogInfo;
};

export const getDeviceIdByType = (searchRequest: DeviceSearchRequest, type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return searchRequest.partId!;
    case DEVICE_MODAL_TYPE.AREA:
      return searchRequest.zoneId!;
    case DEVICE_MODAL_TYPE.SECTION:
      return searchRequest.sectionId!;
    default:
      return searchRequest.equipId!;
  }
};
export const getStrokeColor = (type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return '#c8294b';
    case DEVICE_MODAL_TYPE.AREA:
      return '#3461eb';
    case DEVICE_MODAL_TYPE.SECTION:
      return '#4bb505';
    default:
      return '#000';
  }
}

export const getStrokeColorInStatusBoard = (type: DEVICE_MODAL_TYPE) => {
  switch (type) {
    case DEVICE_MODAL_TYPE.PART:
      return '#000000';
    case DEVICE_MODAL_TYPE.AREA:
      return '#000000';
    case DEVICE_MODAL_TYPE.SECTION:
      return '#6a8fff';
    default:
      return '#000';
  }
}

/**
 * 
 * @param imageRatio 이미지 비율(height/width)
 * @param imageWidth 이미지 width
 * @param windowWidth 윈도우 width
 * @returns scale
 * @description 이미지 비율과 윈도우 width를 이용하여 scale을 계산합니다.
 */
export const getScaleAndRatio = (canvasImage: fabric.Image, windowSize: { width: number, height: number }): { imgRatio: number, scale: number } => {
  const { width, height } = canvasImage;
  if (!width || !height) {
    return {
      imgRatio: 1,
      scale: 1
    }
  }

  const imgRatio = Math.min(height / width, width / height);
  const windowView = width > height ? (windowSize.width - 200) / width : (windowSize.height - 200) / height;
  const scale = Math.max(imgRatio * windowView, MIN_SCALE);

  return {
    imgRatio,
    scale
  }
}

export const createNewRectByEquipment = (info: Equipment, type: DEVICE_MODAL_TYPE) =>
  new fabric.Rect({
    left: 0,
    top: 0,
    width: 100,
    height: 100,
    fill: 'rgba(255, 255, 255, 0.0)',
    stroke: getStrokeColor(type),
    strokeWidth: 2,
    selectable: true,
    name: `${type}/${info.id}`
  });

export const createRectByEquipListAndRatio = (equipList: Equipment[], type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
  equipList.filter(item => item.width > 0)
    .map(item =>
      new fabric.Rect({
        left: Math.round(item.leftPos * ratio),
        top: Math.round(item.topPos * ratio),
        width: Math.round(item.width * ratio),
        height: Math.round(item.height * ratio),
        fill,
        stroke: getStrokeColor(type),
        strokeWidth: 2,
        selectable,
        name: `${type}/${item.id}`,
      })
    );

export const createRectByEquipmentAndRatio = (info: Equipment, type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
  new fabric.Rect({
    left: info.leftPos * ratio,
    top: info.topPos * ratio,
    width: info.width * ratio,
    height: info.height * ratio,
    fill,
    stroke: getStrokeColor(type),
    strokeWidth: 2,
    selectable,
    name: `${type}/${info.id}`,
  });

// export const createRectByEquipmentAndRatioInStatusBoard = (info: Equipment, type: DEVICE_MODAL_TYPE, ratio: number, selectable: boolean, fill: string = 'rgba(255, 255, 255, 0.0)') =>
//   new fabric.Rect({
//     left: info.leftPos * ratio,
//     top: info.topPos * ratio,
//     width: info.width * ratio,
//     height: info.height * ratio,
//     fill,
//     stroke: getStrokeColorInStatusBoard(type),
//     strokeWidth: 0.5,
//     selectable,
//     name: `${type}/${info.id}`,
//   });



export const filterRectByType = (objects: fabric.Object[], type: DEVICE_MODAL_TYPE) => objects.filter(obj => obj.name?.includes(type));

export const generatePopupContent = (content: string): string[] => [
  '<html><head><title></title>',
  `
    <link rel="stylesheet" href="/assets/css/common.css">
    <link rel="stylesheet" href="/assets/css/style.css">
    <link rel="stylesheet" href="/assets/css/layout.css">
    <link rel="stylesheet" href="/assets/css/content.css">
    <link rel="stylesheet" href="/assets/css/fontawesome.min.css">`,
  '</head><body>',
  content,
  '</body></html>',
]

export const emailRegex = /^((?!\.)[\w\-_.]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/gm;


const sqlRegex = /select|from|and|or|union|declare|cast|to|char|having|where|xp_cmdshell|update|into|insert/gmi;
const specialCharRegx = /[{}\[\]\/?.,;:|)*~!^+<>#$%&\=(’"]/gmi;
export const containsSpecialCharAndSql = (msg: string) => sqlRegex.test(msg) || specialCharRegx.test(msg);

export const generateShiftWork = (work1: string, work2: string, work3: string, work4: string): string =>
  `${work1}->${work2}->${work3}->${work4}`;

export const get24Hour = () => {
  const hourList = [];
  for (let i = 0; i < 24; i += 1) {
    hourList.push(i);
  }

  return hourList;
}

export const shiftGroupList: string[] = ['A', 'B', 'C', 'D'];

export const formatPhoneNumber = (value: string) => {
  if (!value) {
    return '';
  }

  const cleaned = value.replace(/\D/g, ''); // 숫자가 아닌 문자는 제거
  const match = cleaned.match(/(\d{3})(\d{4})(\d{4})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  } if (cleaned.length > 7) {
    return cleaned.replace(/(\d{3})(\d{4})(\d{0,4})/, '$1-$2-$3');
  } if (cleaned.length > 3) {
    return cleaned.replace(/(\d{3})(\d{0,4})/, '$1-$2');
  }

  return cleaned;
};

export const isSuperAdmin = (role: string, factoryId: string): boolean => role === ROLE_TYPE.ADMIN && factoryId === '';

export const convertDeptIdToString = (id: DEPT_TYPE | string): string => {
  switch (id) {
    case DEPT_TYPE.PROD:
      return i18next.t('home.prod');
    case DEPT_TYPE.ELEC:
      return i18next.t('home.elec');
    case DEPT_TYPE.MECH:
      return i18next.t('home.mech');
    default:
      return '';
  }
}

const convertRole = (role: number): number => {
  switch (role.toString()) {
    case ROLE_TYPE.ADMIN:
      return role;
    case ROLE_TYPE.MANAGER:
      return 1;
    default:
      return 2;
  }
}

const isOriginHigherRole = (originRole: number, acceptRole: number) => {
  const origin = convertRole(originRole);
  const accept = convertRole(acceptRole);

  return origin <= accept;
}

export const getAcceptUserInfo = (originRole: number, acceptUsers: AcceptUser[]): AcceptUserInfo => {
  const defaultAccept = {
    acceptUserId: '',
    acceptUserRole: '',
    useAcceptRole: false
  };

  if (originRole.toString() !== ROLE_TYPE.USER || !acceptUsers || acceptUsers?.length === 0) {
    return defaultAccept;
  }

  const notUser = acceptUsers.find(user => ROLE_TYPE.USER !== user.role.toString());
  if (notUser && !isOriginHigherRole(originRole, notUser.role)) {
    return {
      acceptUserId: encryptString(notUser.userId),
      acceptUserRole: encryptString(notUser.role),
      useAcceptRole: true
    }
  }

  return defaultAccept;
}

export const isUserRoleByAccept = (userInfo: UserInfo): boolean =>
  userInfo.useAcceptRole
    ? decryptString(userInfo.acceptUserRole) === ROLE_TYPE.USER
    : decryptString(userInfo.role) === ROLE_TYPE.USER;


export const getAcceptInfoByUserInfo = (userInfo: UserInfo): AcceptUserInfo => ({
  acceptUserId: decryptId(userInfo.acceptUserId),
  acceptUserRole: decryptString(userInfo.acceptUserRole),
  useAcceptRole: userInfo.useAcceptRole
})

export const getDiffRole = (role: string, acceptRole: string) => {
  if (!acceptRole) {
    return role;
  }

  return acceptRole;
}

export const convertRoleToString = (role: number | string): string => {
  const target = role.toString();
  if (!target) {
    return '';
  }

  return target === ROLE_TYPE.ADMIN
    ? i18next.t('user.superAdmin')
    : target === ROLE_TYPE.MANAGER
      ? i18next.t('user.admin')
      : i18next.t('user.user');

}

export const isDuplicateIdToArray = (ids: number[]): boolean => {
  const size = ids.length;
  if (size === 0) {
    return true;
  }

  return new Set(ids).size !== size;
}

export const convertLotoTypeToString = (type: LOTO_TYPE | string): string => {
  if (!type) {
    return '';
  }

  return type === LOTO_TYPE.LO ? 'LOTO' : 'TO';
}

export const removeEmptyParams = (params: any) => {
  const result = { ...params };
  Object.keys(result).forEach(key => {
    if (result[key] === null || result[key] === undefined || result[key] === '') {
      delete result[key];
    }
  });

  return result;
}

/**
 * ','로 구분된 email 목록 문자열을 자른 후 수에 따라 외 x개로 반환
 * @param emails ','로 구분된 email 목록 문자열
 */
export const transferEmails = (emails: string): string => {
  if (!emails) {
    return '';
  }
  const separatedEmails = emails.split(',');
  return `${separatedEmails[0]}${separatedEmails.length > 1 ? ` ${i18next.t('conclusion.other')}${separatedEmails.length - 1}` : ''}`;
}